import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, ChevronDown, ChevronRight, Minus } from 'lucide-react'
import { UnifiedButton } from './ui/unified-button'
import { SidebarItem } from '../types'
import { useState } from 'react'

interface DraggableSidebarItemProps {
  section: SidebarItem
  isExpanded: boolean
  onSectionClick: (section: SidebarItem) => void
  onRemoveSection?: (sectionId: string) => void
}

export const DraggableSidebarItem = ({
  section,
  isExpanded,
  onSectionClick,
  onRemoveSection
}: DraggableSidebarItemProps) => {
  const [isHovered, setIsHovered] = useState(false)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isCurrentlyDragging
  } = useSortable({ id: section.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isCurrentlyDragging ? 0.8 : 1,
    zIndex: isCurrentlyDragging ? 1000 : 'auto'
  }

  const Icon = section.icon

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onRemoveSection) {
      onRemoveSection(section.id)
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative w-full ${isCurrentlyDragging ? 'cursor-grabbing' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <UnifiedButton
        variant="ghost"
        className={`h-11 w-full cursor-pointer justify-start gap-3 px-3 transition-all duration-200 ${
          isExpanded ? 'bg-primary/10 text-primary border-primary/20 border' : 'hover:bg-muted'
        } ${isCurrentlyDragging ? 'scale-105 shadow-lg' : ''}`}
        onClick={() => onSectionClick(section)}
      >
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1 truncate text-left">{section.label}</span>
        </div>

        {/* Right side controls - chevron by default, remove/drag on hover */}
        <div className="relative flex items-center gap-1">
          {/* Chevron indicators - shown when not hovering */}
          <div
            className={`transition-opacity duration-200 ${
              isCurrentlyDragging || isHovered ? 'opacity-0' : 'opacity-100'
            }`}
          >
            {isExpanded ? (
              <ChevronDown className="text-muted-foreground h-3 w-3" />
            ) : (
              <ChevronRight className="text-muted-foreground h-3 w-3" />
            )}
          </div>

          {/* Remove button - appears on hover */}
          {onRemoveSection && (
            <div
              className={`absolute right-6 flex items-center justify-center transition-opacity duration-200 ${
                isHovered && !isCurrentlyDragging ? 'opacity-100' : 'opacity-0'
              }`}
              onClick={handleRemoveClick}
            >
              <div className="group/remove cursor-pointer rounded p-1 hover:bg-red-500/20">
                <Minus className="h-3 w-3 text-red-500 group-hover/remove:text-red-400" />
              </div>
            </div>
          )}

          {/* Drag Handle - appears on hover */}
          <div
            {...attributes}
            {...listeners}
            className={`absolute right-0 flex items-center justify-center transition-opacity duration-200 ${
              isHovered || isCurrentlyDragging ? 'opacity-100' : 'opacity-0'
            } cursor-grab active:cursor-grabbing`}
            onClick={(e) => e.stopPropagation()}
            style={{ zIndex: 10 }}
          >
            <div className="p-1">
              <GripVertical className="text-muted-foreground h-3 w-3" />
            </div>
          </div>
        </div>
      </UnifiedButton>
    </div>
  )
}
